import { TotalDetail } from "../../domain/PaymentsSlice/TotalDetail";
import { PromoCodeGet } from "../../domain/PromoCodesSlice/PromoCodeGet";
import { PromoTypeValue } from "../../domain/PromoCodesSlice/PromoTypeValue";
import { RouteGetBase } from "../../domain/RouteSlice/RouteGetBase";
import { ShoppingCartGet } from "../../domain/ShoppingCartSlice/ShoppingCartGet";

export const isVisiblePromo = (promo: PromoCodeGet, isLoggedClient: boolean) => {
  if (!promo.isActive) return false;
  switch (promo.promoType) {
    case PromoTypeValue.MembersOnlyVisible:
      return isLoggedClient;
    case PromoTypeValue.AllowAnonVisible:
      return true;
    default:
      return false; // OneTime, MembersOnly, AllowAnon are hidden/manual
  }
};

export const isManualPromo = (promo: PromoCodeGet) => {
  switch (promo.promoType) {
    case PromoTypeValue.OneTime:
    case PromoTypeValue.MembersOnly:
    case PromoTypeValue.AllowAnon:
      return true;
    default:
      return false;
  }
};

export const canUserTryPromo = (promo: PromoCodeGet, isLoggedClient: boolean) => {
  if (!promo.isActive) return false;
  if (
    promo.promoType === PromoTypeValue.MembersOnly ||
    promo.promoType === PromoTypeValue.MembersOnlyVisible
  ) {
    return isLoggedClient;
  }
  return true;
};

export const meetsMinOrder = (promo: PromoCodeGet, subtotal: number) => {
  const min = promo.minimumOrderAmount ?? 0;
  return subtotal >= min;
};

export const appliesToProduct = (promo: PromoCodeGet, productId: number) =>
  !!promo.productPromoCodes?.some((p) => p.productId === productId);

/**
 * Per-promo, per-unit discount (no eligibility/min checks here).
 * Keeps this as a pure calculation utility.
 */
export const computeOrderDiscount = (promo: PromoCodeGet, subtotal: number) => {
  if (!promo.appliesToOrderTotal) return 0;
  if (promo.discountAmount && promo.discountAmount > 0) return promo.discountAmount;
  const pct = promo.discountPercentage ?? 0;
  return Math.floor((pct / 100) * subtotal);
};

export const computeLineDiscount = (promo: PromoCodeGet, unitPrice: number) => {
  if (promo.appliesToOrderTotal) return 0;
  if (promo.discountAmount && promo.discountAmount > 0) return promo.discountAmount;
  const pct = promo.discountPercentage ?? 0;
  return Math.floor((pct / 100) * unitPrice);
};

export const computePromoSavingsAmount = (promo: PromoCodeGet, subtotal: number): number => {
  return promo.appliesToOrderTotal ? computeOrderDiscount(promo, subtotal) : computeLineDiscount(promo, subtotal);
};

// Given many visible promos, choose the best order-level promo by highest discount value
export const bestOrderPromo = (promos: PromoCodeGet[], subtotal: number) => {
  const candidates = promos.filter(
    (p) => p.isActive && p.appliesToOrderTotal && meetsMinOrder(p, subtotal)
  );
  if (candidates.length === 0) return null;
  return candidates
    .map((p) => ({ p, val: computeOrderDiscount(p, subtotal) }))
    .sort((a, b) => b.val - a.val)[0].p;
};

export const bestOrderPromoReadOnly = (promos: PromoCodeGet[], subtotal: number) => {
  if (!Array.isArray(promos) || promos.length === 0) return null;
  const candidates = promos.filter(
    (p) => p.isActive && p.appliesToOrderTotal
  );

  if (candidates.length === 0) return null;

  const hasAmount = candidates.some((p) => p.discountAmount != null);

  if (hasAmount) {
    // Compare all as amounts (convert percentages to amounts)
    const valued = candidates.map((p) => {
      const pct = p.discountPercentage ?? null;
      const amtFromPct = pct != null ? (subtotal * pct) / 100 : null;
      const amount = p.discountAmount ?? amtFromPct ?? 0;
      return { p, amount };
    });

    return valued.sort((a, b) => b.amount - a.amount)[0].p;
  } else {
    // Only percentage promos -> compare directly
    const valued = candidates.map((p) => ({
      p,
      pct: p.discountPercentage ?? 0,
    }));

    return valued.sort((a, b) => b.pct - a.pct)[0].p;
  }
};



/**
 * NEW:
 * Given a list of promos, compute the best line-level promo (if any) for a product
 * respecting:
 *  - promo is active
 *  - promo is line-level (not appliesToOrderTotal)
 *  - promo applies to the product
 *  - promo meets its minimum order threshold with the provided subtotal
 * Returns the best promo and its per-unit discount (0 if none apply).
 */
export const bestLinePromoForProduct = (
  promos: PromoCodeGet[],
  productId: number,
  unitPrice: number,
  subtotal: number
): { promo: PromoCodeGet | null; discount: number } => {
  let best: PromoCodeGet | null = null;
  let bestDiscount = 0;

  for (const promo of promos) {
    if (!promo.isActive) continue;
    if (promo.appliesToOrderTotal) continue;
    if (!appliesToProduct(promo, productId)) continue;
    if (!meetsMinOrder(promo, subtotal)) continue;

    const discount = computeLineDiscount(promo, unitPrice);
    if (discount > bestDiscount) {
      bestDiscount = discount;
      best = promo;
    }
  }

  return { promo: best, discount: bestDiscount };
};

// Compute totals from scratch (no mutation). Order-level promo wins over line-level visible promos.
// Manual order-level promo overrides visible order-level promos.
export const buildTotals = (
  cart: ShoppingCartGet | null | undefined,
  route: RouteGetBase | undefined,
  orderLevelPromo: PromoCodeGet | null,
  lineLevelPromos: PromoCodeGet[],
  creditBalance: number = 0
): TotalDetail => {
  const transportCost = route?.transportCost ?? 0;

  if (!cart || !cart.shoppingCartItems || cart.shoppingCartItems.length === 0) {
    const baseTotal = transportCost;
    const applied = Math.max(0, Math.min(creditBalance ?? 0, baseTotal));
    const totalAfterCredit = Math.max(0, baseTotal - applied);
    return {
      subtotal: 0,
      totalItemsDiscount: 0,
      total: totalAfterCredit,
      transportCost,
      totalRaw: totalAfterCredit,
      discountRaw: null,
      promoCode: null,
      promoCodeId: null,
      hasDiscount: false,
      promoAppliedToTotal: false,
      promoMinimumOrderAmount: 0,
      creditsApplied: 0,
    };
  }

  let subtotal = 0;
  cart.shoppingCartItems.forEach((it) => {
    subtotal += it.price * it.quantity;
  });

  // If an order-level promo exists, it takes precedence over line promos
  if (orderLevelPromo) {
    const disc = computeOrderDiscount(orderLevelPromo, subtotal);
    const baseTotal = subtotal - disc + transportCost;
    const applied = Math.max(0, Math.min(creditBalance ?? 0, baseTotal));
    const totalAfterCredit = Math.max(0, baseTotal - applied);
    return {
      subtotal,
      totalItemsDiscount: disc,
      total: totalAfterCredit,
      transportCost,
      totalRaw: totalAfterCredit,
      discountRaw: disc,
      promoCodeId: orderLevelPromo.promoCodeId,
      promoCode: orderLevelPromo.code,
      hasDiscount: disc > 0,
      promoAppliedToTotal: true,
      promoMinimumOrderAmount: orderLevelPromo.minimumOrderAmount ?? 0,
      creditsApplied: applied,
    };
  }

  // Otherwise apply the best line-level promo per product (no stacking).
  let lineDiscountSum = 0;
  for (const it of cart.shoppingCartItems) {
    const { discount } = bestLinePromoForProduct(
      lineLevelPromos,           // can include visible + applied line promos
      it.productId,
      it.price,
      subtotal                   // pass subtotal to enforce min order thresholds
    );
    lineDiscountSum += discount * it.quantity;
  }

  const baseTotal = subtotal - lineDiscountSum + transportCost;
  const applied = Math.max(0, Math.min(creditBalance ?? 0, baseTotal));
  const totalAfterCredit = Math.max(0, baseTotal - applied);
  return {
    subtotal,
    totalItemsDiscount: lineDiscountSum,
    total: totalAfterCredit,
    transportCost,
    totalRaw: totalAfterCredit,
    discountRaw: lineDiscountSum,
    promoCodeId: null,
    promoCode: null,
    hasDiscount: lineDiscountSum > 0,
    promoAppliedToTotal: false,
    promoMinimumOrderAmount: 0,
    creditsApplied: applied,
  };
};
